import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, User, Home, Heart, Mail } from "lucide-react";

interface NavbarProps {
    showBackButton?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ showBackButton = false }) => {
    const location = useLocation();
    const isHomePage = location.pathname === "/";
    const isProfilePage = location.pathname === "/profile";
    const isContactPage = location.pathname === "/contact";

    return (
        <>
            {/* Top Navigation Bar */}
            <nav className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40 shadow-sm transition-all">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-14 sm:h-16">
                        {/* Left Section */}
                        <div className="flex items-center space-x-3 sm:space-x-4">
                            {showBackButton && (
                                <Link
                                    to="/"
                                    className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                                    aria-label="Kembali"
                                >
                                    <div className="p-1.5 sm:p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
                                        <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                                    </div>
                                    <span className="ml-2 font-medium text-sm hidden sm:block">
                                        Kembali
                                    </span>
                                </Link>
                            )}

                            {/* Logo */}
                            <Link to="/" className="flex items-center group">
                                <div className="bg-blue-600 group-hover:bg-blue-700 p-1.5 sm:p-2 rounded-xl transition-colors shadow-sm">
                                    <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                                </div>
                                <div className="ml-2.5 sm:ml-3">
                                    <span className="font-bold text-base sm:text-xl text-gray-900 tracking-tight">
                                        Portfolio <span className="text-blue-600 font-extrabold">Evano</span>
                                    </span>
                                </div>
                            </Link>
                        </div>

                        {/* Desktop Navigation Links */}
                        <div className="hidden sm:flex items-center space-x-1 sm:space-x-2">
                            <Link
                                to="/"
                                className={`flex items-center px-3.5 py-2 rounded-xl font-medium text-sm transition-all ${
                                    isHomePage
                                        ? "bg-blue-600 text-white shadow-sm"
                                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                                }`}
                            >
                                <Home className="h-4 w-4 mr-1.5" />
                                <span>Beranda</span>
                            </Link>

                            <Link
                                to="/profile"
                                className={`flex items-center px-3.5 py-2 rounded-xl font-medium text-sm transition-all ${
                                    isProfilePage
                                        ? "bg-blue-600 text-white shadow-sm"
                                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                                }`}
                            >
                                <User className="h-4 w-4 mr-1.5" />
                                <span>Profil</span>
                            </Link>

                            <Link
                                to="/contact"
                                className={`flex items-center px-3.5 py-2 rounded-xl font-medium text-sm transition-all ${
                                    isContactPage
                                        ? "bg-blue-600 text-white shadow-sm"
                                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                                }`}
                            >
                                <Mail className="h-4 w-4 mr-1.5" />
                                <span>Kontak</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Native Mobile Bottom Tab Bar */}
            <div className="sm:hidden fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-md border-t border-gray-200/80 z-50 px-2 py-1.5 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
                <div className="flex items-center justify-around max-w-md mx-auto">
                    <Link
                        to="/"
                        className={`flex flex-col items-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                            isHomePage
                                ? "text-blue-600 font-semibold"
                                : "text-gray-400 hover:text-gray-600"
                        }`}
                    >
                        <div className={`p-1 rounded-xl transition-colors ${isHomePage ? "bg-blue-50" : ""}`}>
                            <Home className="h-5 w-5" />
                        </div>
                        <span className="text-[11px] mt-0.5 tracking-tight">Beranda</span>
                    </Link>

                    <Link
                        to="/profile"
                        className={`flex flex-col items-center py-1 px-4 rounded-2xl transition-all active:scale-95 ${
                            isProfilePage
                                ? "text-blue-600 font-semibold"
                                : "text-gray-400 hover:text-gray-600"
                        }`}
                    >
                        <div className={`p-1 rounded-xl transition-colors ${isProfilePage ? "bg-blue-50" : ""}`}>
                            <User className="h-5 w-5" />
                        </div>
                        <span className="text-[11px] mt-0.5 tracking-tight">Profil</span>
                    </Link>

                    <Link
                        to="/contact"
                        className={`flex flex-col items-center py-1 px-4 rounded-2xl transition-all active:scale-95 ${
                            isContactPage
                                ? "text-blue-600 font-semibold"
                                : "text-gray-400 hover:text-gray-600"
                        }`}
                    >
                        <div className={`p-1 rounded-xl transition-colors ${isContactPage ? "bg-blue-50" : ""}`}>
                            <Mail className="h-5 w-5" />
                        </div>
                        <span className="text-[11px] mt-0.5 tracking-tight">Kontak</span>
                    </Link>
                </div>
            </div>
        </>
    );
};

export default Navbar;
